import React, { useState } from "react";
import {
  Card,
  Checkbox,
  DatePicker,
  TimePicker,
  Button,
  Row,
  Col,
  Typography,
  Space,
  message,
  Input,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useNavigate } from "react-router-dom";
import { createAnnouncement } from "src/services/AdminPortalAPIs";
import ReusableModal from "src/components/reusable/ConfirmationModal";
import StyledTextInput from "src/components/reusable/StyledTextInput";

const { Title, Text } = Typography;
const { TextArea } = Input;

const plans = [
  "Free Plan",
  "Basic Plan",
  "Pro Plan",
  "Enterprise Plan",
  "Inactive Users",
  "New Users (< 30 days)",
];

const CreateAnnouncement: React.FC = () => {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [actionUrl, setActionUrl] = useState("");
  const [content, setContent] = useState("");
  const [targetAudience, setTargetAudience] = useState<string[]>([
    "Basic Plan",
  ]);
  const [publishImmediately, setPublishImmediately] = useState(false);
  const [scheduleDate, setScheduleDate] = useState<Dayjs | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [scheduleTime, setScheduleTime] = useState<Dayjs | null>(
    dayjs()
      .hour(12)
      .minute(0)
  );
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const handleAudienceChange = (checkedValues: any[]) => {
    if (checkedValues.length === 0) {
      message.warning("At least one target audience must be selected.");
    } else {
      setTargetAudience(checkedValues);
    }
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      return message.error("Title is required.");
    }

    if (targetAudience.length === 0) {
      return message.error("Please select at least one audience.");
    }

    const status: "published" | "scheduled" = publishImmediately
      ? "published"
      : "scheduled";

    // Content required for both published and scheduled
    if (!content.trim()) {
      return message.error("Content is required.");
    }

    // For scheduled, validate date/time
    let scheduled_for: string | undefined;
    if (status === "scheduled") {
      if (!scheduleDate || !scheduleTime) {
        return message.error("Schedule date and time are required.");
      }
      scheduled_for = `${scheduleDate.format(
        "YYYY-MM-DD"
      )}T${scheduleTime.format("HH:mm:ss")}.000Z`;
    }

    if (actionUrl && !/^https?:\/\/.+\..+/.test(actionUrl.trim())) {
      return message.error("Please enter a valid URL.");
    }

    const payload = {
      title: title.trim(),
      content: content.trim(),
      target_audience: targetAudience 

        .map(audience =>

          audience === "New Users (< 30 days)" ? "new users" : audience
        )
        .join(", "),
      scheduled_for,
      status,
      ...(actionUrl.trim() && { url: actionUrl.trim() }), 
    };

    try {
      await createAnnouncement(payload);
      message.success("Announcement created!");
      navigate("/admin-portal/announcements");
    } catch (error) {
      message.error("Failed to create announcement.");
    }
  };

  const handleCancelDraft = async () => {
    if (!title.trim()) {
      message.error("Title is required to save as draft.");
      setIsCancelModalOpen(false);
      return;
    }

    if (targetAudience.length === 0) {
      message.error("At least one audience must be selected to save draft.");
      setIsCancelModalOpen(false);
      return;
    }

    try {
      await createAnnouncement({
        title: title.trim(),
        target_audience: targetAudience[0], // single audience as per backend
        status: "draft",
      });
      message.success("Draft saved.");
      navigate("/admin-portal/announcements");
    } catch {
      message.error("Failed to save draft.");
    }
  };

  return (
    <div className="admin-portal-btns">
      <button
        className="send-email-btn"
        onClick={() => navigate("/admin-portal/announcements")}
      >
        <span>&lt;&nbsp;</span> Go Back
      </button>

      <div className="heading-primary">Create Announcement</div>

      <Row
        gutter={24}
        style={{
          display: "flex",
          alignItems: "stretch", // Ensures Cols match height
        }}
      >
        <Col
          span={12}
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Card
            title="Announcement Content"
            headStyle={{
              backgroundColor: "#0077b6",
              color: "#fff",
              fontWeight: "bold",
            }}
            style={{
              border: "1px solid #00b4d8",
              flex: 1, // Ensures card stretches
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Space direction="vertical" size="large" style={{ width: "100%" }}>
              <div>
                <Text strong>Title</Text>
                <StyledTextInput
                  placeholder="Enter announcement title"
                  value={title}
                  
                  onChange={e => setTitle(e.target.value)}
                />
              </div>
              <div>
                <Text strong>Content</Text>
                <TextArea
                  rows={6}
                  placeholder="Enter announcement content..."
                  value={content}
                  onChange={e => setContent(e.target.value)}
                />
              </div>
              <div>
                <Text strong>
                  Action Button URL <Text type="secondary">(optional)</Text>
                </Text>
                <StyledTextInput
                  placeholder="https://example.com/announcement-action"
                  value={actionUrl}
                  onChange={e => setActionUrl(e.target.value)}
                  type="url"
                />
              </div>
            </Space>
          </Card>
        </Col>

        <Col
          span={12}
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Card
            title="Publishing Settings"
            headStyle={{
              backgroundColor: "#0077b6",
              color: "#fff",
              fontWeight: "bold",
            }}
            style={{
              border: "1px solid #00b4d8",
              flex: 1, // Ensures card stretches
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Text strong>Target Audience</Text>
            <div style={{ marginTop: 12, marginBottom: 16 }}>
              <Checkbox.Group
                value={targetAudience}
                onChange={handleAudienceChange}
              >
                <Space direction="vertical">
                  <Checkbox value="All Users">All Users</Checkbox>
                  {plans.map(plan => (
                    <Checkbox key={plan} value={plan}>
                      {plan}
                    </Checkbox>
                  ))}
                </Space>
              </Checkbox.Group>
            </div>

            <Checkbox
              checked={publishImmediately}
              onChange={e => setPublishImmediately(e.target.checked)}
              style={{ marginBottom: 16 }}
            >
              Publish immediately
            </Checkbox>

            {!publishImmediately && (
              <Space direction="vertical" style={{ width: "100%" }}>
                <div>
                  <Text strong>Schedule Date</Text>
                  <DatePicker
                    style={{ width: "100%" }}
                    value={scheduleDate}
                    onChange={setScheduleDate}
                  />
                </div>
                <div>
                  <Text strong>Schedule Time</Text>
                  <TimePicker
                    use12Hours
                    format="h:mm A"
                    style={{ width: "100%" }}
                    value={scheduleTime}
                    onChange={setScheduleTime}
                  />
                </div>
              </Space>
            )}
          </Card>
        </Col>
      </Row>

      <div style={{ marginTop: 24, textAlign: "right" }}>
        <Button
          style={{ marginRight: 12 }}
          aria-label="Cancel" 
          onClick={() => setIsCancelModalOpen(true)}


        >
          Cancel
        </Button>
       <Button
  type="primary"
  aria-label="Schedule"
  onClick={handleSubmit}
  loading={submitting}
  disabled={
    submitting ||
    (!publishImmediately && (!scheduleDate || !scheduleTime))
  }
>
  Schedule
</Button>

      </div>

      <ReusableModal
        modalOpen={isCancelModalOpen}
        onCancel={() => {
          setIsCancelModalOpen(false);
          navigate("/admin-portal/announcements");
        }}
        onConfirm={handleCancelDraft}
        title="Save as draft?"
        description="Do you want to save this announcement as a draft before leaving?"
        icon="/Icons/announcements.svg"
        confirmText="Save as Draft"
        cancelText="Discard"
      />
    </div>
  );
};

export default CreateAnnouncement;
